import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/authService';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';
import { User, SignupData } from '../types';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (userData: SignupData) => Promise<void>;
  quickSignup: (userData: QuickSignupData) => Promise<void>;
  logout: () => void;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  resendVerification: () => Promise<void>;
}


interface QuickSignupData {
  name: string;
  email: string;
  password: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing session on app load
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (token) {
          // Verify token with backend
          const currentUser = await authService.getCurrentUser();
          
          // Check if user is deleted or inactive
          if (currentUser && (currentUser.deleted_at || !currentUser.is_active)) {
            console.log('User account is deleted or inactive, clearing session');
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            localStorage.removeItem('supabase_refresh_token');
            apiService.clearAuthToken();
            setUser(null);
          } else {
            setUser(currentUser);
          }
        }
      } catch (error) {
        console.log('Auth check failed:', error);
        // Token is invalid, clear it
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        apiService.clearAuthToken();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await authService.login({ email, password });
      
      // Store session token and user data
      if (response.session?.access_token) {
        apiService.setAuthToken(response.session.access_token);
        localStorage.setItem('authToken', response.session.access_token);
      }
      if (response.session?.refresh_token) {
        localStorage.setItem('supabase_refresh_token', response.session.refresh_token);
      }
      if (response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
        setUser(response.user);
      }
      
      // Refresh user data to get latest subscription info
      try {
        const currentUser = await authService.getCurrentUser();
        setUser(currentUser);
        localStorage.setItem('user', JSON.stringify(currentUser));
      } catch (error) {
        console.warn('Failed to refresh user data after login:', error);
      }
      
      toast.success('Login successful!');
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Login failed. Please check your credentials.';
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (userData: SignupData) => {
    setIsLoading(true);
    try {
      const response = await authService.signup(userData);
      
      // Store session token and user data
      if (response.session?.access_token) {
        apiService.setAuthToken(response.session.access_token);
      }
      if (response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
        setUser(response.user);
      }
      
      toast.success('Account created successfully! Please check your email for verification.');
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Signup failed. Please try again.';
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const quickSignup = async (userData: QuickSignupData) => {
    setIsLoading(true);
    try {
      const response = await authService.quickSignup(userData);
      
      // Store session token and user data
      if (response.session?.access_token) {
        apiService.setAuthToken(response.session.access_token);
      }
      if (response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
        setUser(response.user);
      }
      
      // Don't show success toast here - let the component handle it
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Account creation failed. Please try again.';
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsLoading(false);
      apiService.clearAuthToken();
      localStorage.removeItem('user');
      localStorage.removeItem('authToken');
      localStorage.removeItem('supabase_refresh_token');
      toast.success('Logged out successfully');
      // Redirect to home page after logout
      window.location.href = '/';
    }
  };

  const forgotPassword = async (email: string) => {
    setIsLoading(true);
    try {
      await authService.forgotPassword(email);
      toast.success('Password reset email sent!');
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to send reset email. Please try again.';
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (token: string, newPassword: string) => {
    setIsLoading(true);
    try {
      await authService.resetPassword(token, newPassword);
      toast.success('Password reset successful!');
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Password reset failed. Please try again.';
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const verifyEmail = async (token: string) => {
    setIsLoading(true);
    try {
      const response = await authService.verifyEmail(token);
      
      // Update step 1 in database to mark email as verified
      if (response.user_id) {
        try {
          await apiService.post('/api/v1/signup-progress/step1', {
            is_verified: true,
            user_id: response.user_id
          });
        } catch (dbError) {
          console.error('Failed to update step 1 in database:', dbError);
          // Don't fail the verification if database update fails
        }
      }
      
      toast.success('Email verified successfully!');
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Email verification failed.';
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const resendVerification = async () => {
    setIsLoading(true);
    try {
      await authService.resendVerification();
      toast.success('Verification email sent!');
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to send verification email.';
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    login,
    signup,
    quickSignup,
    logout,
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerification,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 